import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Star, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Avis {
  id: string;
  prenom: string;
  nom: string;
  date: string;
  note: number;
  commentaire: string;
}

const Reviews = () => {
  const [avis, setAvis] = useState<Avis[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAvis = async () => {
      const { data, error } = await supabase
        .from('avis')
        .select('*')
        .order('date', { ascending: false });

      if (error) {
        console.error('Error fetching avis:', error);
      } else if (data) {
        setAvis(data);
      }
      setIsLoading(false);
    };

    fetchAvis();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // Calculate statistics
  const totalAvis = avis.length;
  const averageNote = totalAvis > 0 
    ? (avis.reduce((sum, item) => sum + item.note, 0) / totalAvis).toFixed(1)
    : '0.0';
  
  const noteDistribution = [5, 4, 3, 2, 1].map(note => ({
    note,
    count: avis.filter(item => item.note === note).length,
    percentage: totalAvis > 0 ? (avis.filter(item => item.note === note).length / totalAvis) * 100 : 0
  }));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mb-4 text-center">
            Vos avis
          </h1>
          <p className="text-lg text-muted-foreground text-center mb-12">
            Découvrez ce que nos clients pensent de Yumeli
          </p>

          {isLoading ? (
            <p className="text-center text-muted-foreground">Chargement...</p>
          ) : avis.length === 0 ? (
            <p className="text-center text-muted-foreground">Aucun avis pour le moment.</p>
          ) : (
            <>
              {/* Statistics Section */}
              <Card className="p-8 mb-12 shadow-warm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left: Average Rating */}
                  <div className="flex flex-col items-center justify-center border-r-0 md:border-r border-border">
                    <div className="text-6xl font-bold text-foreground mb-2">{averageNote}</div>
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-6 h-6 ${
                            i < Math.round(parseFloat(averageNote))
                              ? 'fill-primary text-primary'
                              : 'text-muted-foreground'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground">
                      Basé sur {totalAvis} avis
                    </p>
                  </div>

                  {/* Right: Rating Distribution */}
                  <div className="space-y-3">
                    {noteDistribution.map(({ note, count, percentage }) => (
                      <div key={note} className="flex items-center gap-3">
                        <div className="flex items-center gap-1 w-16">
                          <span className="text-sm font-medium text-foreground">{note}</span>
                          <Star className="w-4 h-4 fill-primary text-primary" />
                        </div>
                        <Progress value={percentage} className="flex-1 h-2" />
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Reviews Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {avis.map((item) => (
                  <Card key={item.id} className="p-6 shadow-warm">
                    <div className="flex justify-center mb-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                    <div className="flex gap-1 mb-3 justify-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < item.note
                              ? 'fill-primary text-primary'
                              : 'text-muted-foreground'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-foreground mb-4 text-sm text-center">{item.commentaire}</p>
                    <p className="text-sm text-muted-foreground text-center">
                      {item.prenom} {item.nom.charAt(0)}. - {formatDate(item.date)}
                    </p>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Reviews;
